import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { money, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PublicLayout } from "@/components/PublicLayout";
import { StatusBadge, fmtDate, daysLeft } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Tv, Clock, ShoppingBag, Bell, User, CheckCircle2, Zap } from "lucide-react";

export default function CustomerDashboard() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [profile, setProfile] = useState({ name: user?.name || "", phone: user?.phone || "", password: "" });

  const { data: subs = [] } = useQuery({ queryKey: ["my-subs"], queryFn: async () => (await api.get("/subscriptions/my")).data });
  const { data: orders = [] } = useQuery({ queryKey: ["my-orders"], queryFn: async () => (await api.get("/orders/my")).data });
  const { data: notifications = [] } = useQuery({ queryKey: ["my-notifs"], queryFn: async () => (await api.get("/notifications/my")).data });

  const activeSub = subs.find((s) => s.status === "active");

  const saveProfile = useMutation({
    mutationFn: async () => (await api.put("/auth/profile", profile)).data,
    onSuccess: () => { toast.success("Profile updated"); refresh(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const markRead = useMutation({
    mutationFn: async (id) => api.put(`/notifications/${id}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["my-notifs"] }),
  });

  const stats = [
    { icon: Zap, label: "Active Plans", value: subs.filter((s) => s.status === "active").length, color: "text-emerald-400" },
    { icon: ShoppingBag, label: "Total Orders", value: orders.length, color: "text-neon-blue" },
    { icon: Clock, label: "Days Remaining", value: activeSub ? daysLeft(activeSub.expiry_date) : 0, color: "text-neon-purple" },
  ];

  return (
    <PublicLayout>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="font-heading text-3xl font-extrabold text-white mb-1">Hi, {user?.name?.split(" ")[0]} 👋</h1>
        <p className="text-slate-400 mb-8">Manage your subscription and orders here.</p>

        {/* Active subscription banner */}
        <div className="glass rounded-2xl p-6 mb-8 relative overflow-hidden" data-testid="active-subscription-card">
          <div className="absolute inset-0 bg-gradient-to-r from-purple-600/15 to-blue-600/10" />
          <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center shadow-[0_0_20px_rgba(147,51,234,0.5)]">
                <Tv className="w-7 h-7 text-white" />
              </div>
              <div>
                {activeSub ? (
                  <>
                    <div className="text-white font-heading text-xl font-bold">{activeSub.package_name} · {activeSub.duration_label}</div>
                    <div className="text-sm text-slate-400 flex items-center gap-2 mt-1">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" /> Active until {fmtDate(activeSub.expiry_date)} · {daysLeft(activeSub.expiry_date)} days left
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-white font-heading text-xl font-bold">No active subscription</div>
                    <div className="text-sm text-slate-400 mt-1">Choose a plan to start streaming today.</div>
                  </>
                )}
              </div>
            </div>
            <Button data-testid="dashboard-browse-plans" onClick={() => navigate("/packages")} className="glow-btn bg-gradient-to-r from-purple-600 to-blue-600 text-white border-0">
              {activeSub ? "Renew / Upgrade" : "Browse Plans"}
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
          {stats.map((s) => (
            <div key={s.label} className="glass rounded-2xl p-5 flex items-center gap-4">
              <s.icon className={`w-8 h-8 ${s.color}`} />
              <div>
                <div className="font-heading text-2xl font-extrabold text-white">{s.value}</div>
                <div className="text-xs text-slate-400">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="subscriptions">
          <TabsList className="bg-white/5 border border-white/10">
            <TabsTrigger value="subscriptions" data-testid="tab-subscriptions">Subscriptions</TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders">Order History</TabsTrigger>
            <TabsTrigger value="notifications" data-testid="tab-notifications">
              Notifications{notifications.filter((n) => !n.read).length ? ` (${notifications.filter((n) => !n.read).length})` : ""}
            </TabsTrigger>
            <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
          </TabsList>

          <TabsContent value="subscriptions" className="mt-6">
            <div className="glass rounded-2xl overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-slate-400">Package</TableHead>
                    <TableHead className="text-slate-400">Start</TableHead>
                    <TableHead className="text-slate-400">Expiry</TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {subs.length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="text-center text-slate-500 py-10">No subscriptions yet.</TableCell></TableRow>
                  ) : subs.map((s) => (
                    <TableRow key={s.id} className="border-white/10" data-testid={`sub-row-${s.id}`}>
                      <TableCell className="text-white font-medium">{s.package_name}</TableCell>
                      <TableCell className="text-slate-400">{fmtDate(s.start_date)}</TableCell>
                      <TableCell className="text-slate-400">{fmtDate(s.expiry_date)}</TableCell>
                      <TableCell><StatusBadge status={s.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="orders" className="mt-6">
            <div className="glass rounded-2xl overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-slate-400">Order</TableHead>
                    <TableHead className="text-slate-400">Date</TableHead>
                    <TableHead className="text-slate-400">Amount</TableHead>
                    <TableHead className="text-slate-400">Payment</TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-10">No orders yet.</TableCell></TableRow>
                  ) : orders.map((o) => (
                    <TableRow key={o.id} className="border-white/10" data-testid={`order-row-${o.id}`}>
                      <TableCell className="text-white font-medium">{o.package_name}<div className="text-xs text-slate-500">{o.duration_label}</div></TableCell>
                      <TableCell className="text-slate-400">{fmtDate(o.created_at)}</TableCell>
                      <TableCell className="text-white">{money(o.amount, o.currency)}</TableCell>
                      <TableCell><StatusBadge status={o.payment_status} /></TableCell>
                      <TableCell><StatusBadge status={o.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="notifications" className="mt-6 space-y-3">
            {notifications.length === 0 ? (
              <div className="glass rounded-2xl p-10 text-center text-slate-500">No notifications.</div>
            ) : notifications.map((n) => (
              <div key={n.id} data-testid={`notif-${n.id}`} onClick={() => !n.read && markRead.mutate(n.id)}
                className={`glass rounded-xl p-4 flex items-start gap-3 cursor-pointer ${!n.read ? "border-purple-500/40" : ""}`}>
                <Bell className={`w-5 h-5 mt-0.5 ${!n.read ? "text-neon-purple" : "text-slate-500"}`} />
                <div className="flex-1">
                  <div className="text-white font-medium text-sm">{n.title}</div>
                  <div className="text-slate-400 text-sm mt-0.5">{n.message}</div>
                  <div className="text-xs text-slate-600 mt-1">{fmtDate(n.created_at)}</div>
                </div>
                {!n.read && <span className="w-2 h-2 rounded-full bg-neon-purple mt-2" />}
              </div>
            ))}
          </TabsContent>

          <TabsContent value="profile" className="mt-6">
            <div className="glass rounded-2xl p-7 max-w-lg">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-white font-bold">
                  {user?.name?.[0]}
                </div>
                <div>
                  <div className="text-white font-semibold">{user?.name}</div>
                  <div className="text-sm text-slate-400">{user?.email}</div>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-300">Full Name</Label>
                  <Input data-testid="profile-name" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className="mt-1.5 bg-white/5 border-white/10 text-white" />
                </div>
                <div>
                  <Label className="text-slate-300">Phone</Label>
                  <Input data-testid="profile-phone" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} className="mt-1.5 bg-white/5 border-white/10 text-white" />
                </div>
                <div>
                  <Label className="text-slate-300">New Password (leave blank to keep)</Label>
                  <Input data-testid="profile-password" type="password" value={profile.password} onChange={(e) => setProfile({ ...profile, password: e.target.value })} className="mt-1.5 bg-white/5 border-white/10 text-white" />
                </div>
                <Button data-testid="profile-save" onClick={() => saveProfile.mutate()} disabled={saveProfile.isPending} className="glow-btn bg-gradient-to-r from-purple-600 to-blue-600 text-white border-0">
                  Save Changes
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </section>
    </PublicLayout>
  );
}
